<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useTicketsStore } from 'dashboard/stores/tickets';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import TicketAttachmentsInput from './TicketAttachmentsInput.vue';

const emit = defineEmits(['created']);

const { t } = useI18n();
const store = useTicketsStore();
const { currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');

const dialogRef = ref(null);
const attachmentsInput = ref(null);

const buildForm = () => ({
  ticketTypeId: null,
  title: '',
  startedOn: '',
  description: '',
  contactPhone: '',
  notificationEmail: currentUser.value.email,
  attachments: [],
});
const form = reactive(buildForm());

const canSubmit = computed(
  () => form.ticketTypeId && form.title.trim() && form.description.trim()
);

const open = async () => {
  Object.assign(form, buildForm());
  dialogRef.value.open();
  await store.fetchSummary();
  form.ticketTypeId = store.ticketTypes[0]?.id ?? null;
};

const onPaste = event => attachmentsInput.value?.onPaste(event);

const submit = async () => {
  if (!canSubmit.value) return;
  try {
    const ticket = await store.createTicket({
      ticket_type_id: form.ticketTypeId,
      title: form.title.trim(),
      description: form.description.trim(),
      started_on: form.startedOn,
      contact_phone: form.contactPhone.trim(),
      notification_email: form.notificationEmail.trim(),
      attachments: form.attachments,
    });
    useAlert(t('TICKETS.NEW.SUCCESS', { displayId: ticket.displayId }));
    dialogRef.value.close();
    emit('created', ticket);
  } catch (error) {
    useAlert(error?.response?.data?.message || t('TICKETS.NEW.ERROR'));
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    position="top"
    overflow-y-auto
    :title="t('TICKETS.NEW.TITLE')"
    :description="
      t('TICKETS.NEW.DESCRIPTION', { accountName: currentAccount?.name })
    "
    :confirm-button-label="t('TICKETS.NEW.SUBMIT')"
    :disable-confirm-button="!canSubmit"
    :is-loading="store.uiFlags.isCreating"
    @confirm="submit"
  >
    <div class="flex flex-col gap-4" @paste="onPaste">
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('TICKETS.NEW.TYPE') }}
        </span>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            v-for="ticketType in store.ticketTypes"
            :key="ticketType.id"
            type="button"
            class="flex flex-col items-start gap-0.5 p-3 text-start rounded-lg outline outline-1 -outline-offset-1"
            :class="
              form.ticketTypeId === ticketType.id
                ? 'outline-n-blue-9 bg-n-alpha-1'
                : 'outline-n-weak hover:outline-n-slate-6'
            "
            @click="form.ticketTypeId = ticketType.id"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ ticketType.name }}
            </span>
            <span v-if="ticketType.description" class="text-xs text-n-slate-11">
              {{ ticketType.description }}
            </span>
          </button>
        </div>
      </div>
      <Input
        v-model="form.title"
        :label="t('TICKETS.NEW.SUMMARY')"
        :placeholder="t('TICKETS.NEW.SUMMARY_PLACEHOLDER')"
        autofocus
      />
      <Input
        v-model="form.startedOn"
        type="date"
        :label="t('TICKETS.NEW.STARTED_ON')"
        class="sm:w-1/2"
      />
      <TextArea
        v-model="form.description"
        :label="t('TICKETS.NEW.DETAILS')"
        :placeholder="t('TICKETS.NEW.DETAILS_PLACEHOLDER')"
        auto-height
        min-height="7rem"
        max-height="16rem"
      />
      <div class="flex flex-col gap-1">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="form.contactPhone"
            type="tel"
            :label="t('TICKETS.NEW.CONTACT_PHONE')"
            :placeholder="t('TICKETS.NEW.CONTACT_PHONE_PLACEHOLDER')"
          />
          <Input
            v-model="form.notificationEmail"
            type="email"
            :label="t('TICKETS.NEW.NOTIFICATION_EMAIL')"
          />
        </div>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('TICKETS.NEW.NOTIFICATION_EMAIL_HELP') }}
        </p>
      </div>
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('TICKETS.ATTACHMENTS.LABEL') }}
        </span>
        <TicketAttachmentsInput
          ref="attachmentsInput"
          v-model="form.attachments"
        />
      </div>
    </div>
  </Dialog>
</template>
